import { type Game, type Schedule } from "@aura-frontend/omnibus-az-client";
import { Dialog, Label, Select, toast } from "@win-reality/react-ui";
import { useEffect, useMemo, useState } from "react";

import { TimeSlotAvailability } from "@/components/EventForm/TimeSlotAvailability/TimeSlotAvailability";
import { useComplexes } from "@/hooks/useComplexes";
import { formatHHMM } from "@/utils/dateUtils";

import { useMoveEvent } from "../FieldSchedule/scheduleMutations.queries";

function getGameFromSchedule(schedule: Schedule): Game | null {
  return schedule.merged?.game ?? null;
}

function getScheduleTimeRange(schedule: Schedule): {
  start: number;
  end: number;
} {
  const game = getGameFromSchedule(schedule);
  if (game?.times?.length) {
    const firstTime = game.times[0];
    const lastTime = game.times.at(-1);
    const start = firstTime?.startTime ?? schedule.schedule_time_s;
    const end =
      lastTime?.endTime ?? firstTime?.startTime ?? schedule.schedule_time_s;

    return { start, end };
  }
  return { start: schedule.schedule_time_s, end: schedule.schedule_time_s };
}

export type MoveEventDialogProps = {
  event: Schedule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function MoveEventDialog({
  event,
  open,
  onOpenChange,
}: MoveEventDialogProps) {
  const { complexes } = useComplexes();
  const { moveEvent, isMovingEvent } = useMoveEvent();

  const currentComplexName = useMemo(
    () =>
      complexes?.find((c) =>
        c.fields.some((f) => f.machineUuid === event.machine_uuid),
      )?.name,
    [complexes, event.machine_uuid],
  );

  const [selectedComplexName, setSelectedComplexName] = useState<
    string | undefined
  >(undefined);
  const [selectedMachineUuid, setSelectedMachineUuid] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (open) {
      setSelectedComplexName(currentComplexName);
      setSelectedMachineUuid(undefined);
    }
  }, [open, currentComplexName]);

  const complexItems = useMemo(
    () => (complexes ?? []).map((c) => ({ label: c.name, value: c.name })),
    [complexes],
  );

  const fieldItems = useMemo(() => {
    const selected = complexes?.find((c) => c.name === selectedComplexName);
    return (selected?.fields ?? [])
      .filter((f) => f.machineUuid !== event.machine_uuid)
      .map((f) => ({ label: f.name, value: f.machineUuid }));
  }, [complexes, selectedComplexName, event.machine_uuid]);

  const { start, end } = getScheduleTimeRange(event);
  const eventStartDate = useMemo(() => new Date(start * 1000), [start]);
  const eventStartTime = formatHHMM(start * 1000);
  const eventEndTime = formatHHMM(end * 1000);

  const [isSlotAvailable, setIsSlotAvailable] = useState(true);

  const isDisabled =
    !selectedMachineUuid ||
    selectedMachineUuid === event.machine_uuid ||
    !isSlotAvailable;

  const handleSubmit = async () => {
    if (!selectedMachineUuid) return;
    const targetField = complexes
      ?.find((c) => c.name === selectedComplexName)
      ?.fields.find((f) => f.machineUuid === selectedMachineUuid);
    try {
      await moveEvent(event, selectedMachineUuid);
      toast(`Event moved to ${targetField?.name ?? "new field"}`, {
        variant: "success",
      });
      onOpenChange(false);
    } catch {
      toast("Failed to move event", { variant: "error" });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title="Move Fields"
      primaryAction={{
        label: "Move Event",
        onClick: handleSubmit,
        isDisabled,
        isLoading: isMovingEvent,
      }}
    >
      <div className="flex flex-col gap-4">
        <div>
          <Label className="mb-2 text-sm">
            Complex<span className="text-error">*</span>
          </Label>
          <Select
            items={complexItems}
            value={selectedComplexName}
            onValueChange={(v) => {
              setSelectedComplexName(v);
              setSelectedMachineUuid(undefined);
            }}
            placeholder="Select complex"
          />
        </div>

        <div>
          <Label className="mb-2 text-sm">
            New Field<span className="text-error">*</span>
          </Label>
          <Select
            items={fieldItems}
            value={selectedMachineUuid}
            onValueChange={(v) => setSelectedMachineUuid(v)}
            placeholder="Select new field"
            isDisabled={!selectedComplexName || fieldItems.length === 0}
          />
        </div>

        {selectedMachineUuid && (
          <TimeSlotAvailability
            fieldId={selectedMachineUuid}
            startDate={eventStartDate}
            startTime={eventStartTime}
            endTime={eventEndTime}
            onAvailabilityChange={setIsSlotAvailable}
          />
        )}
      </div>
    </Dialog>
  );
}
